import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { ImageGenerationAccessState } from "@/features/image-generation";
import {
  createBillingPortalSession,
  createCheckoutSession,
  getBillingOverview,
  refreshCreditWallet,
} from "@/features/billing/api/billingClient";
import { buildBillingPageState } from "@/features/billing/billingOffers";
import type {
  BillingOfferCode,
  BillingOverview,
  BillingPageState,
  BillingPortalRequest,
} from "@/features/billing/billing.types";
import {
  fetchRuntimePricingCatalog,
  getRuntimePricingCatalogSnapshot,
  type RuntimePricingCatalog,
} from "@/features/pricing/runtimePricing";

export interface SubscriptionStoreDependencies {
  createBillingPortalSession: typeof createBillingPortalSession;
  createCheckoutSession: typeof createCheckoutSession;
  fetchRuntimePricingCatalog: typeof fetchRuntimePricingCatalog;
  getBillingOverview: typeof getBillingOverview;
  refreshCreditWallet: typeof refreshCreditWallet;
}

export interface SubscriptionStoreState {
  applyImageAccessState: (
    accessState: Pick<ImageGenerationAccessState, "creditsRemaining" | "tier">,
  ) => void;
  billingError: string | null;
  billingOverview: BillingOverview | null;
  billingState: BillingPageState | null;
  checkout: (offerCode: BillingOfferCode, returnPath?: string) => Promise<void>;
  creditBalance: number;
  hydrateBilling: () => Promise<void>;
  hydratePricing: () => Promise<void>;
  isBillingLoading: boolean;
  openBillingPortal: (request?: BillingPortalRequest) => Promise<void>;
  pricingCatalog: RuntimePricingCatalog;
  refreshCredits: () => Promise<void>;
  setCreditBalance: (balance: number) => void;
  setTier: (tier: BillingPageState["currentTier"]) => void;
  tier: BillingPageState["currentTier"];
}

const defaultDependencies: SubscriptionStoreDependencies = {
  createBillingPortalSession,
  createCheckoutSession,
  fetchRuntimePricingCatalog,
  getBillingOverview,
  refreshCreditWallet,
};

export function createSubscriptionStore(
  deps: SubscriptionStoreDependencies = defaultDependencies,
) {
  return createStore<SubscriptionStoreState>((set, get) => ({
    billingError: null,
    billingOverview: null,
    billingState: null,
    creditBalance: 0,
    isBillingLoading: false,
    pricingCatalog: getRuntimePricingCatalogSnapshot(),
    tier: "free",

    hydrateBilling: async () => {
      set({
        billingError: null,
        isBillingLoading: true,
      });

      try {
        const [billingOverview, pricingCatalog] = await Promise.all([
          deps.getBillingOverview(),
          deps.fetchRuntimePricingCatalog(),
        ]);
        const billingState = buildBillingPageState(
          billingOverview,
          pricingCatalog.billingOffers,
        );

        set({
          billingOverview,
          billingState,
          creditBalance: billingOverview.wallet.balance,
          isBillingLoading: false,
          pricingCatalog,
          tier: billingOverview.subscription.tier,
        });
      } catch (error) {
        set({
          billingError: error instanceof Error ? error.message : "Billing load failed.",
          isBillingLoading: false,
        });
      }
    },

    hydratePricing: async () => {
      const pricingCatalog = await deps.fetchRuntimePricingCatalog();
      const billingOverview = get().billingOverview;

      set({
        billingState:
          billingOverview === null
            ? get().billingState
            : buildBillingPageState(billingOverview, pricingCatalog.billingOffers),
        pricingCatalog,
      });
    },

    refreshCredits: async () => {
      const wallet = await deps.refreshCreditWallet();
      const billingOverview = get().billingOverview;
      const billingState = get().billingState;

      set({
        billingOverview:
          billingOverview === null
            ? null
            : {
                ...billingOverview,
                wallet,
              },
        billingState:
          billingState === null
            ? null
            : {
                ...billingState,
                nextResetAt: wallet.allocationResetAt,
                wallet,
              },
        creditBalance: wallet.balance,
      });
    },

    applyImageAccessState: (accessState) => {
      const nextState: Partial<SubscriptionStoreState> = {};

      if (typeof accessState.creditsRemaining === "number") {
        const billingOverview = get().billingOverview;
        const billingState = get().billingState;

        nextState.billingOverview =
          billingOverview === null
            ? null
            : {
                ...billingOverview,
                wallet: {
                  ...billingOverview.wallet,
                  balance: accessState.creditsRemaining,
                },
              };
        nextState.billingState =
          billingState === null
            ? null
            : {
                ...billingState,
                wallet: {
                  ...billingState.wallet,
                  balance: accessState.creditsRemaining,
                },
              };
        nextState.creditBalance = accessState.creditsRemaining;
      }

      if (accessState.tier) {
        nextState.tier = accessState.tier;
      }

      if (Object.keys(nextState).length > 0) {
        set(nextState);
      }
    },

    setCreditBalance: (balance) => {
      const billingOverview = get().billingOverview;
      const billingState = get().billingState;

      set({
        billingOverview:
          billingOverview === null
            ? null
            : {
                ...billingOverview,
                wallet: {
                  ...billingOverview.wallet,
                  balance,
                },
              },
        billingState:
          billingState === null
            ? null
            : {
                ...billingState,
                wallet: {
                  ...billingState.wallet,
                  balance,
                },
              },
        creditBalance: balance,
      });
    },

    setTier: (tier) => {
      const billingOverview = get().billingOverview;
      const billingState = get().billingState;

      set({
        billingOverview:
          billingOverview === null
            ? null
            : {
                ...billingOverview,
                subscription: {
                  ...billingOverview.subscription,
                  tier,
                },
              },
        billingState:
          billingState === null
            ? null
            : {
                ...billingState,
                currentTier: tier,
              },
        tier,
      });
    },

    checkout: async (offerCode, returnPath = "/account/billing") => {
      const offer = get().pricingCatalog.billingOffers.find((item) => item.offerCode === offerCode);

      if (!offer) {
        throw new Error(`Unknown billing offer: ${offerCode}`);
      }

      const response = await deps.createCheckoutSession({
        mode: offer.kind === "top_up" ? "payment" : "subscription",
        offerCode,
        returnPath,
      });

      if (typeof window !== "undefined") {
        window.location.assign(response.url);
      }
    },

    openBillingPortal: async (request = {}) => {
      const response = await deps.createBillingPortalSession(request);

      if (typeof window !== "undefined") {
        window.location.assign(response.url);
      }
    },
  }));
}

export const subscriptionStore = createSubscriptionStore();

export function useSubscriptionStore<T>(selector: (state: SubscriptionStoreState) => T) {
  return useStore(subscriptionStore, selector);
}
